import { Injectable } from '@angular/core';
import { Document, DocumentType, Customer, Shareholder, Member } from '@app/core/stingray-api/models';
import { CompaniesService, CollaborationsService } from '@app/core/stingray-api/services';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class FilesService {

    constructor(private companiesService: CompaniesService,
        private collaborationsService: CollaborationsService,
        private translateService: TranslateService) {

    }

    
      /**
       * Returns the text that displays where the document is attached
       * @param document The document to extract the link tekst from
       */
      public getLinkText(document: Document, isCustomerView: boolean): string {
        let linkText = '';
    
        if (document.buildings.length === 1) {
          linkText = document.buildings[0].address.addressLine1;
        }
        if (document.buildings.length > 1) {
          linkText = this.translateService.instant('Multiple buildings') + ' (' + document.buildings.length + ')';
        }
    
        if (document.mortgages.length === 1) {
          linkText = (document.mortgages[0].number || '...') + ' - ' + document.mortgages[0].lender.name;
        }
        if (document.mortgages.length > 1) {
          linkText = this.translateService.instant('Multiple mortgages') + ' (' + document.mortgages.length + ')';
        }
    
        if (isCustomerView || linkText === '' && document.documentType) {
          linkText = this.translateService.instant('To') + ' ' + document.customer.name;
        }
    
        if (linkText) {
          if (document.applications.length === 1) {
            linkText = linkText + ' ' + this.translateService.instant('and application:') + ' ' + document.applications[0].name;
          }
          if (document.applications.length > 1) {
            linkText = linkText + ' ' + this.translateService.instant('and multiple applications') + ' (' + document.applications.length + ')';
          }
        } else {
          if (document.applications.length === 1) {
            linkText = document.applications[0].name;
          }
          if (document.applications.length > 1) {
            linkText = this.translateService.instant('Multiple applications') + ' (' + document.applications.length + ')';
          }
        }
    
        return linkText !== '' ? linkText : null;
      }

    /**
     * Extracts the extension from the file name
     * @param fileName The file name to extract the extension from
     */
    public getFileExtension(fileName: string): string {
        return fileName.substr(fileName.lastIndexOf('.'), fileName.length - fileName.lastIndexOf('.')).toLowerCase();
    }

    /**
     * Check if the given associationType is a customer
     * @param associationType selected associationType
     */
    public isCustomer(associationType: 'Building' | 'Mortgage' | 'Customer' | 'Person' | 'Company' | 'Collaboration') {
        if (associationType === 'Customer' ||
            associationType === 'Person' ||
            associationType === 'Company' ||
            associationType === 'Collaboration') {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Get the related customers
     * @param customer selected customer
     */
    public getRelatedCustomers(customer: Customer): Observable<Customer[]> {
        switch (customer.type) {
            case 'Person': {
                return of([customer]);
            }
            case 'Company': {
                return this.companiesService.getCompanyShareholdersById({ id: customer.id }).pipe(map((shareholders: Shareholder[]) => {
                    const customers = shareholders.map(shareholder => <Customer>{
                        id: shareholder.customerId,
                        type: 'Person', name: shareholder.name
                    });
                    customers.unshift(customer);

                    return customers;
                }));
            }
            case 'Collaboration': {
                return this.collaborationsService.getCollaborationMembers({ id: customer.id }).pipe(map((members: Member[]) => {
                    const customers = members.map(member => <Customer>{
                        id: member.customerId,
                        type: member.type,
                        name: member.name
                    });
                    customers.unshift(customer);

                    return customers;
                }));
            }
        }
    }

    /**
     * Remove the inrelevant document types and sort the types on the id column
     * e.g. a customer with the type 'person' doesn't need to see document types with the association 'company'
     * @param customer selected customer
     * @param documentTypes list with all document types
     */
    public removeInrelevantDocumentTypes(customer: Customer, documentTypes: DocumentType[]): DocumentType[] {
        return documentTypes.filter((documentType: DocumentType) => {
            if (documentType.associationType === 'Building' ||
                documentType.associationType === 'Mortgage' ||
                documentType.associationType === 'Customer') {
                return true;
            }

            if (customer.type === 'Collaboration') {
                return true;
            }

            if (customer.type === 'Company' &&
                (documentType.associationType === 'Company' || documentType.associationType === 'Person')) {
                return true;
            }

            if (customer.type === 'Person' && documentType.associationType === 'Person') {
                return true;
            }

            return false;
        }).sort((documentTypeA, documentTypeB) => (+documentTypeA.id < +documentTypeB.id ? -1 : 1));
    }
}
